import { type ComponentType, Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { type DocumentFormRegistry, resolveDocumentFormRegistryEntry } from './registry';
import ConfirmationDialogV2 from '../confirmation-dialog-v2';
import LoadingOverlayV2 from '../loading-overlay-v2';
import { useCRUD } from '@/hooks';

export interface DocumentFormV2Props {
  document_uuid: string;
  document_type: string;
  onClose: () => void;
  onSubmit?: (data: any) => void;
  registry: DocumentFormRegistry;
  initialFormMode?: 'add' | 'edit' | 'view';
  loadingMessage?: string;
  emptyState?: ReactNode;
}

export default function DocumentFormV2({
  document_uuid,
  document_type,
  onClose,
  onSubmit,
  registry,
  initialFormMode = 'view',
  loadingMessage,
  emptyState = null
}: DocumentFormV2Props) {
  const registryEntry = useMemo(
    () => resolveDocumentFormRegistryEntry(document_type, registry),
    [document_type, registry]
  );
  const endpoint = registryEntry?.endpoint || '';
  const { detail, fetchDetail, addItem, updateItem, deleteItem, isDetailLoading } = useCRUD({
    endpoint,
    initialFetch: false
  });
  const [formMode, setFormMode] = useState<'add' | 'edit' | 'view'>(initialFormMode);
  const [entryDetail, setEntryDetail] = useState<any>(null);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [LoadedFormComponent, setLoadedFormComponent] = useState<ComponentType<any> | null>(null);

  useEffect(() => {
    setFormMode(initialFormMode);
    setIsCopyMode(false);
  }, [initialFormMode, document_type]);

  useEffect(() => {
    let active = true;

    setLoadedFormComponent(null);

    if (!registryEntry) return;

    if (registryEntry.FormComponent) {
      const FormComponent = registryEntry.FormComponent;
      setLoadedFormComponent(() => FormComponent);
      return;
    }

    if (registryEntry.loadFormComponent) {
      registryEntry.loadFormComponent().then(module => {
        if (active) setLoadedFormComponent(() => module.default);
      });
    }

    return () => {
      active = false;
    };
  }, [registryEntry]);

  useEffect(() => {
    if (document_uuid && endpoint && registryEntry?.controller !== 'external') {
      fetchDetail(document_uuid);
    }
  }, [document_uuid, endpoint, fetchDetail, registryEntry?.controller]);

  const Provider = registryEntry?.Provider ?? Fragment;
  const FormComponent = LoadedFormComponent;
  const resolvedDetail = entryDetail ?? detail;
  const formInitialData = formMode === 'add' && !isCopyMode ? undefined : resolvedDetail;

  const extractSubmitPayload = (values: any) => {
    if (values && typeof values === 'object' && 'data' in values) {
      return values.data;
    }

    return values;
  };

  const buildCopyData = useCallback((source: any) => {
    if (!source || typeof source !== 'object') return source;

    const { uuid: _uuid, id: _id, document_uuid: _documentUuid, ...copyData } = source;
    return copyData;
  }, []);

  const handleAdd = useCallback(() => {
    setIsCopyMode(false);
    setEntryDetail(null);
    setFormMode('add');
  }, []);

  const handleEdit = useCallback(async () => {
    setIsCopyMode(false);

    if (resolvedDetail?.uuid) {
      const latestDetail = await fetchDetail(resolvedDetail.uuid);
      setEntryDetail(latestDetail);
    }

    setFormMode('edit');
  }, [fetchDetail, resolvedDetail]);

  const handleCopy = useCallback(() => {
    setIsCopyMode(true);
    setEntryDetail(buildCopyData(resolvedDetail));
    setFormMode('add');
  }, [buildCopyData, resolvedDetail]);

  const handleDelete = useCallback(() => {
    setDeleteOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (values: any) => {
      const payload = extractSubmitPayload(values);
      let nextDetail = resolvedDetail;

      if (formMode === 'add') {
        nextDetail = await addItem(payload);
      } else if (formMode === 'edit' && resolvedDetail?.uuid) {
        nextDetail = await updateItem(resolvedDetail.uuid, payload);
      }

      setEntryDetail(nextDetail);
      setIsCopyMode(false);
      setFormMode('view');
      onSubmit?.(nextDetail);
    },
    [addItem, formMode, onSubmit, resolvedDetail, updateItem]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!resolvedDetail?.uuid) return;

    await deleteItem(resolvedDetail.uuid);
    setDeleteOpen(false);
    onClose();
  }, [deleteItem, onClose, resolvedDetail]);

  if (!registryEntry) return emptyState;
  if (!FormComponent) {
    return (
      <div className='relative h-full min-h-0 w-full'>
        <LoadingOverlayV2 message={loadingMessage} />
      </div>
    );
  }

  if (registryEntry.controller !== 'external' && isDetailLoading) {
    return (
      <div className='relative h-full min-h-0 w-full'>
        <LoadingOverlayV2 message={loadingMessage} />
      </div>
    );
  }

  if (registryEntry.controller === 'external') {
    return (
      <Provider>
        <FormComponent
          document_uuid={document_uuid}
          document_type={document_type}
          onClose={onClose}
          onSubmit={onSubmit ?? (() => {})}
          onEdit={() => setFormMode('edit')}
          onAdd={() => setFormMode('add')}
          onView={() => setFormMode('view')}
          formMode={formMode}
          initialData={detail}
        />
      </Provider>
    );
  }

  return (
    <Provider>
      <FormComponent
        document_uuid={document_uuid}
        document_type={document_type}
        onClose={onClose}
        onSubmit={handleSubmit}
        onEdit={handleEdit}
        onAdd={handleAdd}
        onView={() => setFormMode('view')}
        onDelete={handleDelete}
        onCopy={handleCopy}
        formMode={formMode}
        initialData={formInitialData}
      />
      <ConfirmationDialogV2
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        selectedObj={resolvedDetail}
        title='Xóa chứng từ'
        message='Bạn có chắc chắn muốn xóa chứng từ này không?'
        confirmButtonText='Xóa'
        cancelButtonText='Hủy'
      />
    </Provider>
  );
}
